<template>
  <section class="wx-category custom-el-wrapper" aria-label="微信类目管理">
    <h1 class="wx-category__sr-only">微信类目</h1>
    <div class="wx-category__shop-filter">
      <label class="wx-category__shop-filter-label" for="wechat-category-shop">当前店铺</label>
      <el-select id="wechat-category-shop" v-model="baseShopId" name="wechat_category_shop" size="small" filterable automatic-dropdown default-first-option autocomplete="off" aria-label="当前微信店铺（点击后可搜索店铺名）" no-match-text="暂无匹配店铺" no-data-text="暂无微信店铺" placeholder="输入店铺名搜索…" :disabled="preflighting || submitting" @change="onShopChange">
        <el-option v-for="shop in shops" :key="shop.shop_id" :label="shop.shop_name" :value="shop.shop_id" />
      </el-select>
      <span class="wx-category__shop-search-hint">点击下拉后可输入店铺名搜索</span>
    </div>

    <div class="wx-category__tabs" role="tablist" aria-label="微信类目功能">
      <button v-for="tab in tabs" :key="tab.key" type="button" role="tab" :aria-selected="activeTab === tab.key" :class="{ on: activeTab === tab.key }" @click="changeTab(tab.key)">{{ tab.label }}</button>
    </div>

    <template v-if="activeTab === 'apply'">
      <div class="wx-category__toolbar">
        <label class="wx-category__sr-only" for="wechat-category-keyword">搜索微信小店类目</label>
        <el-input id="wechat-category-keyword" v-model="filters.keyword" name="wechat_category_keyword" autocomplete="off" aria-label="搜索微信小店类目" size="small" clearable placeholder="搜索微信小店类目…" @change="loadCatalog" @clear="loadCatalog" />
        <label class="wx-category__sr-only" for="wechat-category-deposit">保证金门槛</label>
        <el-select id="wechat-category-deposit" v-model="filters.deposit_limit" aria-label="保证金门槛" size="small" clearable placeholder="保证金门槛…" @change="loadCatalog">
          <el-option v-for="limit in depositLimits" :key="limit" :label="limit + ' 元以内'" :value="limit" />
        </el-select>
        <span class="wx-category__sync-hint" role="status" aria-live="polite">若类目已申请，点此 <button type="button" class="wx-category__sync" :disabled="syncing" @click="syncCatalog">{{ syncing ? '更新中…' : '同步' }}</button></span>
      </div>

      <div v-loading="loadingCatalog" :aria-busy="loadingCatalog" class="wx-category__picker-card">
        <div class="wx-category__picker-head">
          <h2 class="wx-category__picker-title">类目选择</h2>
          <span class="wx-category__picker-tools"><span v-if="catalog.last_synced_at" class="muted">规则已同步</span><button type="button" @click="selectAllVisibleCategories">全选可申请类目</button><button type="button" @click="clearCategories">清空</button></span>
        </div>
        <div class="wx-category__columns">
          <div v-for="column in categoryColumns" :key="column.level" class="wx-category__column">
            <div class="wx-category__column-head">
              <label class="wx-category__column-select">
                <input type="checkbox" :checked="columnState(column).checked" :indeterminate.prop="columnState(column).indeterminate" :disabled="columnState(column).disabled" :aria-label="levelName(column.level) + '全选'" @change.stop="toggleColumn(column, $event.target.checked)">
                <span>{{ levelName(column.level) }}全选</span>
              </label>
              <span>所需保证金</span>
            </div>
            <div class="wx-category__column-list">
              <div v-if="!column.nodes.length" class="wx-category__column-empty">{{ column.level === 1 ? '暂无符合条件的类目' : '请选择' + levelName(column.level - 1) }}</div>
              <div v-for="node in column.nodes" :key="node.category_id" class="wx-category__category-row" :class="{ active: activePath[column.level - 1] === String(node.category_id), disabled: nodeCheckableCount(node) === 0 }" role="button" tabindex="0" :aria-disabled="nodeCheckableCount(node) === 0" :aria-label="node.category_name + (node.children && node.children.length ? '，展开下级类目' : '')" @click="activateCategory(node, column.level - 1)" @keydown.enter="activateCategory(node, column.level - 1)" @keydown.space.prevent="activateCategory(node, column.level - 1)">
                <input type="checkbox" :checked="isNodeChecked(node)" :indeterminate.prop="isNodeIndeterminate(node)" :disabled="nodeCheckableCount(node) === 0" :aria-label="'选择' + node.category_name" @click.stop @change.stop="toggleNode(node, $event.target.checked)">
                <span class="wx-category__category-name" :title="node.category_name">{{ node.category_name }}<em v-if="nodeChildCount(node) > 0"> ({{ nodeChildCount(node) }})</em></span>
                <span class="wx-category__category-deposit">{{ depositRangeText(node) }}</span>
                <span v-if="node.children && node.children.length" class="wx-category__category-arrow" aria-hidden="true">›</span>
                <button v-if="node.qualification_mandatory === 1" type="button" class="wx-category__tag" aria-label="查看行业资质要求" title="该类目申请需要上传证照资料" @click.stop="qualificationHint">需行业资质</button>
                <span v-if="node.disabled_reason && node.is_leaf && node.disabled_reason !== 'qualification_required'" class="wx-category__disabled">{{ reasonText(node.disabled_reason) }}</span>
              </div>
            </div>
          </div>
        </div>
        <div class="wx-category__switches">
          <el-checkbox v-model="filters.hide_effective" @change="loadCatalog">过滤已有权限的类目</el-checkbox>
          <el-checkbox v-model="filters.hide_qualification" @change="loadCatalog">过滤需要填写行业资质的类目</el-checkbox>
          <el-checkbox v-model="filters.hide_applying" @change="loadCatalog">过滤申请中的类目</el-checkbox>
          <span class="wx-category__qualification-note">带“需行业资质”标记的类目，请到微信小店后台进行申请上传</span>
        </div>
      </div>

      <div class="wx-category__footer">
        <span class="wx-category__footer-summary" role="status" aria-live="polite"><span>已选类目 <b>{{ selectedCategoryIds.length }}</b> 个</span><span>已选店铺 <b>{{ selectedShopIds.length }}</b> 家</span><span>预计组合 <b>{{ selectedCategoryIds.length * selectedShopIds.length }}</b> 条</span></span>
        <button type="button" class="secondary" :disabled="!selectedCategoryIds.length || preflighting || submitting" @click="openShopSelector">多店铺申请</button><button type="button" class="primary" :class="{ 'is-loading': preflighting || submitting }" :disabled="!selectedCategoryIds.length || !baseShopId || preflighting || submitting" :aria-busy="preflighting || submitting ? 'true' : 'false'" @click="openCurrentShopPreflight"><span v-if="preflighting || submitting" class="wx-category__button-spinner" aria-hidden="true" />{{ preflighting ? '校验中…' : (submitting ? '提交中…' : '立即申请') }}</button>
      </div>
    </template>

    <template v-else-if="activeTab === 'records'">
      <div class="wx-category__record-toolbar"><label class="wx-category__sr-only" for="wechat-application-category-keyword">搜索申请类目</label><el-input id="wechat-application-category-keyword" v-model="recordFilters.category_keyword" name="wechat_application_category_keyword" autocomplete="off" aria-label="搜索申请类目" size="small" clearable placeholder="搜索类目…" @change="loadRecords" /><label class="wx-category__sr-only" for="wechat-application-status">申请状态</label><el-select id="wechat-application-status" v-model="recordFilters.status" aria-label="申请状态" size="small" clearable placeholder="全部状态…" @change="loadRecords"><el-option v-for="status in recordStatuses" :key="status.value" :label="status.label" :value="status.value" /></el-select><label class="wx-category__sr-only" for="wechat-application-date">申请日期范围</label><el-date-picker id="wechat-application-date" v-model="recordFilters.submitted_range" aria-label="申请日期范围" type="daterange" size="small" range-separator="至" start-placeholder="申请开始…" end-placeholder="申请结束…" value-format="yyyy-MM-dd" @change="loadRecords" /><button type="button" :disabled="!selectedRecordIds.length || retrying" @click="retrySelected">批量重新申请</button></div>
      <div class="wx-category__table-wrap" :aria-busy="loadingRecords"><el-table v-loading="loadingRecords" :data="records" size="small" @selection-change="handleRecordSelection"><el-table-column type="selection" width="48" :selectable="recordSelectable" /><el-table-column prop="category_id" label="类目 ID" width="100" /><el-table-column prop="shop_name" label="店铺" width="180"><template slot-scope="scope">{{ shopName(scope.row) }}</template></el-table-column><el-table-column prop="category_path" label="类目" min-width="300" /><el-table-column label="申请时间" width="165"><template slot-scope="scope">{{ formatApplicationTime(scope.row.created_at) }}</template></el-table-column><el-table-column prop="status" label="状态" width="110"><template slot-scope="scope"><span :class="'status-' + scope.row.status">{{ statusText(scope.row.status) }}</span></template></el-table-column><el-table-column prop="failure_reason" label="失败原因" min-width="220" show-overflow-tooltip /><el-table-column label="操作" width="100"><template slot-scope="scope"><el-button v-if="scope.row.status === 'failed'" type="text" @click="retry([scope.row.id])">重新申请</el-button></template></el-table-column></el-table></div>
      <el-pagination v-if="recordTotal" layout="total, prev, pager, next" :total="recordTotal" :page-size="recordPageSize" :current-page.sync="recordPage" @current-change="loadRecords" />
    </template>

    <template v-else>
      <div class="wx-category__toolbar">
        <label class="wx-category__sr-only" for="wechat-effective-category-keyword">搜索已生效类目</label>
        <el-input id="wechat-effective-category-keyword" v-model="effectiveKeyword" name="wechat_effective_category_keyword" autocomplete="off" aria-label="搜索已生效类目" size="small" clearable placeholder="搜索已生效类目…" @change="loadEffectiveCatalog" @clear="loadEffectiveCatalog" />
        <span class="wx-category__sync-hint" role="status" aria-live="polite">若类目已变更，点此 <button type="button" class="wx-category__sync" :disabled="loadingEffective" @click="syncEffectiveCatalog">同步</button></span>
      </div>

      <div v-loading="loadingEffective" :aria-busy="loadingEffective" class="wx-category__picker-card">
        <div class="wx-category__picker-head"><h2 class="wx-category__picker-title">已生效类目</h2><span v-if="effectiveCatalog.shop" class="muted">{{ effectiveCatalog.shop.shop_name }}</span></div>
        <div class="wx-category__columns">
          <div v-for="column in effectiveCategoryColumns" :key="column.level" class="wx-category__column">
            <div class="wx-category__column-head"><span>{{ levelName(column.level) }}全选</span><span>所需保证金</span></div>
            <div class="wx-category__column-list">
              <div v-if="!column.nodes.length" class="wx-category__column-empty">{{ column.level === 1 ? '暂无已生效类目' : '请选择' + levelName(column.level - 1) }}</div>
              <div v-for="node in column.nodes" :key="node.category_id" class="wx-category__category-row" :class="{ active: effectiveActivePath[column.level - 1] === String(node.category_id) }" role="button" tabindex="0" :aria-label="node.category_name + (node.children && node.children.length ? '，展开下级类目' : '')" @click="activateEffectiveCategory(node, column.level - 1)" @keydown.enter="activateEffectiveCategory(node, column.level - 1)" @keydown.space.prevent="activateEffectiveCategory(node, column.level - 1)">
                <span class="wx-category__category-name" :title="node.category_name">{{ node.category_name }}<em v-if="nodeChildCount(node) > 0"> ({{ nodeChildCount(node) }})</em></span>
                <span class="wx-category__category-deposit">{{ depositRangeText(node) }}</span>
                <span v-if="node.children && node.children.length" class="wx-category__category-arrow" aria-hidden="true">›</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>

    <el-dialog title="选择申请店铺" :visible.sync="shopDialogVisible" width="680px" :close-on-click-modal="!preflighting" :close-on-press-escape="!preflighting" :show-close="!preflighting">
      <div class="wx-category__shops wx-category__shops-dialog">
        <div class="wx-category__shops-head"><span>申请店铺</span><span>{{ selectedShopIds.length }}/20</span></div>
        <div class="wx-category__shop-controls">
          <label class="wx-category__sr-only" for="wechat-application-shop-keyword">搜索申请店铺</label>
          <el-input id="wechat-application-shop-keyword" v-model="shopKeyword" name="wechat_application_shop_keyword" autocomplete="off" aria-label="搜索申请店铺" size="small" clearable placeholder="搜索店铺…" :disabled="preflighting" />
          <label class="wx-category__sr-only" for="wechat-application-group">店铺分组</label>
          <el-select id="wechat-application-group" v-model="groupId" aria-label="店铺分组" size="small" clearable placeholder="全部分组…" class="group-select" :disabled="preflighting">
            <el-option v-for="group in groups" :key="group" :label="group || '未分组'" :value="group" />
          </el-select>
          <button type="button" :disabled="preflighting" @click="selectCurrentShops">全选当前结果</button><button type="button" :disabled="preflighting" @click="selectedShopIds = []">清空</button>
        </div>
        <el-checkbox-group v-model="selectedShopIds" class="wx-category__shop-list" :disabled="preflighting">
          <el-checkbox v-for="shop in filteredShops" :key="shop.shop_id" :label="shop.shop_id" :disabled="Number(shop.status) !== 1">
            <span>{{ shop.shop_name }}</span><small>{{ Number(shop.status) === 1 ? '授权正常' : '授权异常' }}</small>
          </el-checkbox>
        </el-checkbox-group>
      </div>
      <span slot="footer"><span class="muted">已选 {{ selectedShopIds.length }} 家店铺</span><el-button :disabled="preflighting" @click="shopDialogVisible = false">取消</el-button><el-button type="primary" :loading="preflighting" :disabled="preflighting || !selectedShopIds.length" :aria-busy="preflighting ? 'true' : 'false'" @click="confirmShopSelection">{{ preflighting ? '校验中…' : '下一步' }}</el-button></span>
    </el-dialog>

    <el-dialog title="申请预检" :visible.sync="preflightVisible" width="620px" custom-class="wx-category-dialog" :close-on-click-modal="!submitting" :close-on-press-escape="!submitting" :show-close="!submitting">
      <div v-if="preflight"><div class="wx-category__summary"><b>{{ preflight.eligible_count }}</b> 条可申请，<b>{{ preflight.skipped_count }}</b> 条将跳过</div><div class="wx-category__reason-list"><span v-for="(count, reason) in preflight.reason_counts" :key="reason">{{ reasonText(reason) }}：{{ count }}</span></div><p v-if="preflight.reason_counts && preflight.reason_counts.qualification_required" class="wx-category__qualification-note wx-category__preflight-hint">需行业资质的类目，请到微信小店后台进行申请上传。</p><el-table :data="preflight.results" max-height="280" size="mini"><el-table-column prop="mer_shop_id" label="店铺" width="80" /><el-table-column prop="category_path" label="类目" /><el-table-column label="预检结果" width="120"><template slot-scope="scope">{{ reasonText(scope.row.status) }}</template></el-table-column></el-table></div>
      <span slot="footer"><el-button :disabled="submitting" @click="preflightVisible = false">取消</el-button><el-button type="primary" :loading="submitting" :disabled="submitting || !preflight || !preflight.eligible_count" :aria-busy="submitting ? 'true' : 'false'" @click="submitBatch">{{ submitting ? '提交中…' : '确认申请' }}</el-button></span>
    </el-dialog>

    <el-dialog title="提示" :visible.sync="successVisible" width="560px" custom-class="wx-category-success-dialog">
      <p class="wx-category__success-message">提交成功，请前往申请结果查看进度</p>
      <span slot="footer"><el-button @click="successVisible = false">取消</el-button><el-button type="primary" @click="goToRecords">前往</el-button></span>
    </el-dialog>
  </section>
</template>

<script>
import { ddShopList } from '../../api/main/ucenter'
import { getWechatCategoryCatalog, preflightWechatCategory, createWechatCategoryBatch, getWechatCategoryApplications, retryWechatCategoryApplications, syncWechatCategoryCatalog, getWechatCategoryCatalogSyncStatus, syncWechatEffectiveCategories } from '../../api/main/wechatCategory'

export default {
  name: 'WechatCategoryManager',
  data () {
    return {
      tabs: [{ key: 'apply', label: '申请类目' }, { key: 'records', label: '申请记录' }, { key: 'effective', label: '已生效类目' }],
      activeTab: 'apply', shops: [], baseShopId: '', groups: [], groupId: '', shopKeyword: '', tree: [], activePath: [], selectedCategoryIds: [], catalog: {}, loadingCatalog: false, syncing: false,
      effectiveKeyword: '', effectiveTree: [], effectiveActivePath: [], effectiveCatalog: {}, loadingEffective: false,
      selectedShopIds: [], filters: { keyword: '', deposit_limit: '', hide_qualification: true, hide_effective: true, hide_applying: true },
      depositLimits: [1000, 2000, 3000, 4000, 5000, 10000, 20000, 50000, 100000, 150000],
      preflight: null, preflightRequest: null, preflightVisible: false, shopDialogVisible: false, preflighting: false, submitting: false, successVisible: false,
      records: [], selectedRecordIds: [], retrying: false, recordTotal: 0, recordPage: 1, recordPageSize: 20, loadingRecords: false, recordFilters: { mer_shop_id: '', status: '', category_keyword: '', submitted_range: [] },
      recordStatuses: [{ value: 'queued', label: '排队中' }, { value: 'reviewing', label: '审核中' }, { value: 'succeeded', label: '已通过' }, { value: 'failed', label: '失败' }, { value: 'skipped', label: '已跳过' }]
    }
  },
  computed: {
    categoryColumns () {
      const columns = []
      let nodes = Array.isArray(this.tree) ? this.tree : []
      for (let level = 1; level <= 4; level += 1) {
        columns.push({ level, nodes })
        const activeId = this.activePath[level - 1]
        const activeNode = nodes.find(node => String(node.category_id) === String(activeId))
        nodes = activeNode && Array.isArray(activeNode.children) ? activeNode.children : []
      }
      return columns
    },
    effectiveCategoryColumns () {
      const columns = []
      let nodes = Array.isArray(this.effectiveTree) ? this.effectiveTree : []
      for (let level = 1; level <= 4; level += 1) {
        columns.push({ level, nodes })
        const activeId = this.effectiveActivePath[level - 1]
        const activeNode = nodes.find(node => String(node.category_id) === String(activeId))
        nodes = activeNode && Array.isArray(activeNode.children) ? activeNode.children : []
      }
      return columns
    },
    filteredShops () { const keyword = this.shopKeyword.toLowerCase(); return this.shops.filter(shop => (!keyword || String(shop.shop_name || '').toLowerCase().includes(keyword)) && (!this.groupId || String(shop.group_name || '') === String(this.groupId))) },
  },
  mounted () { this.loadShops() },
  methods: {
    loadShops () { ddShopList({ shop_type: 7, page: 1, page_size: 1000 }).then(res => { const data = res.data || {}; const list = data.list && data.list.list ? data.list.list : (Array.isArray(data.list) ? data.list : []); this.shops = list; this.groups = [...new Set(list.map(item => item.group_name || ''))]; if (!this.baseShopId && list.length) { this.baseShopId = list[0].shop_id; this.recordFilters.mer_shop_id = this.baseShopId; if (this.activeTab === 'effective') this.loadEffectiveCatalog(); else if (this.activeTab === 'records') this.loadRecords(); else this.loadCatalog() } }).catch(() => this.$message.error('店铺列表加载失败')) },
    loadCatalog () { if (!this.baseShopId) return; this.loadingCatalog = true; getWechatCategoryCatalog({ mer_shop_id: this.baseShopId, ...this.filters }).then(res => { if (res.code === 200) { this.catalog = res.data || {}; this.tree = this.catalog.tree || []; this.pruneSelectedCategories(); this.normalizeActivePath() } else this.$message.error(res.msg || res.message || '类目目录加载失败') }).finally(() => { this.loadingCatalog = false }) },
    onShopChange () {
      this.selectedShopIds = []
      this.selectedCategoryIds = []
      this.activePath = []
      this.effectiveActivePath = []
      this.effectiveTree = []
      this.effectiveCatalog = {}
      this.recordFilters.mer_shop_id = this.baseShopId
      this.$nextTick(() => {
        if (this.activeTab === 'apply') this.loadCatalog()
        else if (this.activeTab === 'effective') this.loadEffectiveCatalog()
        else this.loadRecords()
      })
    },
    loadEffectiveCatalog () {
      if (!this.baseShopId) return
      this.loadingEffective = true
      this.effectiveTree = []
      this.effectiveCatalog = {}
      getWechatCategoryCatalog({
        mer_shop_id: this.baseShopId,
        keyword: this.effectiveKeyword,
        hide_qualification: false,
        hide_effective: false,
        hide_applying: false,
        deposit_limit: null
      }).then(res => {
        if (res.code !== 200) {
          this.$message.error(res.msg || res.message || '已生效类目加载失败')
          return
        }
        this.effectiveCatalog = res.data || {}
        this.effectiveTree = this.effectiveOnlyTree(this.effectiveCatalog.tree || [])
        this.normalizeEffectivePath()
      }).finally(() => { this.loadingEffective = false })
    },
    effectiveOnlyTree (nodes) {
      return (Array.isArray(nodes) ? nodes : []).reduce((result, node) => {
        const children = this.effectiveOnlyTree(node.children || [])
        if (node.is_leaf ? node.relation_status !== 'effective' : children.length === 0) return result
        const copy = { ...node, children }
        if (!copy.is_leaf) {
          const leaves = this.leafNodes(copy)
          const deposits = leaves.map(leaf => Number(leaf.deposit)).filter(value => Number.isFinite(value) && value >= 0)
          copy.visible_leaf_count = leaves.length
          copy.deposit_min = deposits.length ? Math.min(...deposits) : null
          copy.deposit_max = deposits.length ? Math.max(...deposits) : null
        }
        result.push(copy)
        return result
      }, [])
    },
    normalizeEffectivePath () {
      const path = []
      let nodes = Array.isArray(this.effectiveTree) ? this.effectiveTree : []
      for (let index = 0; index < 4 && nodes.length; index += 1) {
        const current = nodes.find(node => String(node.category_id) === String(this.effectiveActivePath[index])) || nodes[0]
        path.push(String(current.category_id))
        nodes = Array.isArray(current.children) ? current.children : []
      }
      this.effectiveActivePath = path
    },
    activateEffectiveCategory (node, level) {
      const path = this.effectiveActivePath.slice(0, level)
      path[level] = String(node.category_id)
      this.effectiveActivePath = path
    },
    selectCurrentShops () { this.selectedShopIds = this.filteredShops.filter(shop => Number(shop.status) === 1).slice(0, 20).map(shop => shop.shop_id) },
    selectAllVisibleCategories () { const ids = this.leafIds(this.tree); if (ids.length > 100) this.$message.warning('最多选择100个叶子类目'); this.selectedCategoryIds = ids.slice(0, 100) },
    clearCategories () { this.selectedCategoryIds = [] },
    normalizeActivePath () {
      const path = []
      let nodes = Array.isArray(this.tree) ? this.tree : []
      for (let index = 0; index < 4 && nodes.length; index += 1) {
        const current = nodes.find(node => String(node.category_id) === String(this.activePath[index])) || nodes[0]
        path.push(String(current.category_id))
        nodes = Array.isArray(current.children) ? current.children : []
      }
      this.activePath = path
    },
    pruneSelectedCategories () {
      const visibleIds = new Set(this.leafIds(this.tree))
      this.selectedCategoryIds = this.selectedCategoryIds.filter(id => visibleIds.has(String(id))).slice(0, 100)
    },
    activateCategory (node, level) {
      const path = this.activePath.slice(0, level)
      path[level] = String(node.category_id)
      this.activePath = path
    },
    leafNodes (nodeOrNodes) {
      const nodes = Array.isArray(nodeOrNodes) ? nodeOrNodes : [nodeOrNodes]
      return nodes.reduce((result, node) => {
        if (!node) return result
        if (node.is_leaf) {
          // 类目选择只负责收集叶子 ID；保证金、资质和店铺权限由提交前预检统一判断。
          result.push(node)
          return result
        }
        return result.concat(this.leafNodes(node.children || []))
      }, [])
    },
    leafIds (nodeOrNodes) { return this.leafNodes(nodeOrNodes).map(node => String(node.category_id)) },
    nodeChildCount (node) { return Array.isArray(node && node.children) ? node.children.length : 0 },
    allLeafCount (nodeOrNodes) {
      const nodes = Array.isArray(nodeOrNodes) ? nodeOrNodes : [nodeOrNodes]
      return nodes.reduce((count, node) => count + (node && node.is_leaf ? 1 : this.allLeafCount(node && node.children ? node.children : [])), 0)
    },
    nodeCheckableCount (node) { return this.leafNodes(node).length },
    isNodeChecked (node) {
      const ids = this.leafIds(node)
      return ids.length > 0 && ids.every(id => this.selectedCategoryIds.includes(id))
    },
    isNodeIndeterminate (node) {
      const ids = this.leafIds(node)
      const selected = ids.filter(id => this.selectedCategoryIds.includes(id)).length
      return selected > 0 && selected < ids.length
    },
    columnState (column) {
      const ids = this.leafIds(column.nodes)
      const selected = ids.filter(id => this.selectedCategoryIds.includes(id)).length
      return { checked: ids.length > 0 && selected === ids.length, indeterminate: selected > 0 && selected < ids.length, disabled: ids.length === 0 }
    },
    toggleNode (node, checked) {
      const ids = this.leafIds(node)
      const selected = new Set(this.selectedCategoryIds)
      ids.forEach(id => (checked ? selected.add(id) : selected.delete(id)))
      this.selectedCategoryIds = Array.from(selected).slice(0, 100)
    },
    toggleColumn (column, checked) { this.toggleNode({ children: column.nodes }, checked) },
    levelName (level) { return ({ 1: '一级', 2: '二级', 3: '三级', 4: '四级' })[level] || '上级类目' },
    depositRangeText (node) {
      if (node.deposit === null && node.deposit_min === null) return '--'
      const format = value => (Number(value) / 100).toFixed(0)
      const min = node.is_leaf ? node.deposit : node.deposit_min
      const max = node.is_leaf ? node.deposit : node.deposit_max
      return Number(min) === Number(max) ? format(min) + '元' : format(min) + '-' + format(max) + '元'
    },
    changeTab (tab) {
      this.activeTab = tab
      if (tab === 'records') {
        this.recordFilters.mer_shop_id = this.baseShopId
        this.loadRecords()
      } else if (tab === 'effective') {
        this.loadEffectiveCatalog()
      }
    },
    openShopSelector () {
      if (this.preflighting || this.submitting) return
      if (!this.selectedShopIds.length && this.baseShopId) this.selectedShopIds = [this.baseShopId]
      this.shopDialogVisible = true
    },
    confirmShopSelection () {
      if (this.preflighting || this.submitting) return
      if (!this.selectedShopIds.length) {
        this.$message.warning('请至少选择一家店铺')
        return
      }
      this.openPreflight(true)
    },
    openCurrentShopPreflight () {
      if (this.preflighting || this.submitting) return
      if (!this.baseShopId) {
        this.$message.warning('请先选择当前店铺')
        return
      }
      this.selectedShopIds = [this.baseShopId]
      this.openPreflight()
    },
    openPreflight (closeShopOnSuccess = false) {
      if (this.preflighting || this.submitting) return
      const request = {
        mer_shop_ids: [...this.selectedShopIds],
        category_ids: [...this.selectedCategoryIds],
        deposit_limit: this.filters.deposit_limit || null
      }
      if (!request.category_ids.length || !request.mer_shop_ids.length) {
        this.$message.warning('请选择申请类目和店铺')
        return
      }
      if (request.category_ids.length > 100 || request.mer_shop_ids.length > 20 || request.category_ids.length * request.mer_shop_ids.length > 1000) {
        this.$message.warning('超出批量申请限制')
        return
      }
      this.preflighting = true
      this.preflight = null
      this.preflightRequest = null
      preflightWechatCategory(request).then(res => {
        if (res.code === 200) {
          this.preflight = res.data
          this.preflightRequest = request
          if (closeShopOnSuccess) this.shopDialogVisible = false
          this.preflightVisible = true
        } else {
          this.$message.error(res.msg || res.message || '申请预检失败，请稍后重试')
        }
      }).catch(() => {
        this.$message.error('申请预检失败，请检查网络后重试')
      }).finally(() => {
        this.preflighting = false
      })
    },
    submitBatch () {
      if (this.submitting || !this.preflightRequest) return
      this.submitting = true
      const key = 'wx-category-' + Date.now() + '-' + Math.random().toString(36).slice(2)
      createWechatCategoryBatch({ idempotency_key: key, ...this.preflightRequest }).then(res => {
        if (res.code === 200 && res.data && res.data.batch_id) {
          this.preflightVisible = false
          this.successVisible = true
          this.preflightRequest = null
          this.clearCategories()
        } else {
          this.$message.warning((res.data && res.data.preflight && '没有可申请的类目') || res.msg || res.message || '申请提交失败，请稍后重试')
        }
      }).catch(() => {
        this.$message.error('申请请求失败，请检查网络后重试')
      }).finally(() => {
        this.submitting = false
      })
    },
    goToRecords () { this.successVisible = false; this.changeTab('records') },
    loadRecords () { this.loadingRecords = true; this.selectedRecordIds = []; const params = { ...this.recordFilters, page: this.recordPage, page_size: this.recordPageSize }; if (this.recordFilters.submitted_range && this.recordFilters.submitted_range.length === 2) { params.submitted_from = this.recordFilters.submitted_range[0] + ' 00:00:00'; params.submitted_to = this.recordFilters.submitted_range[1] + ' 23:59:59' } delete params.submitted_range; getWechatCategoryApplications(params).then(res => { if (res.code === 200) { const data = res.data || {}; this.records = data.list || []; this.recordTotal = Number(data.total || 0) } }).finally(() => { this.loadingRecords = false }) },
    handleRecordSelection (rows) { this.selectedRecordIds = rows.filter(row => row.status === 'failed').map(row => row.id) },
    recordSelectable (row) { return row.status === 'failed' },
    retrySelected () { this.retry(this.selectedRecordIds) },
    retry (ids) { const applicationIds = (Array.isArray(ids) ? ids : [ids]).filter(Boolean); if (!applicationIds.length || this.retrying) return; this.retrying = true; retryWechatCategoryApplications({ application_ids: applicationIds }).then(res => { if (res.code === 200) { this.selectedRecordIds = []; this.$message.success('已创建重新申请批次'); this.loadRecords() } else this.$message.error(res.msg || res.message) }).catch(() => { this.$message.error('重新申请请求失败，请稍后重试') }).finally(() => { this.retrying = false }) },
    shopName (row) { const name = String(row && row.shop_name || '').trim(); if (name) return name; const shopId = String(row && row.mer_shop_id || ''); const shop = this.shops.find(item => String(item.shop_id || item.mer_shop_id) === shopId); return shop ? shop.shop_name : shopId },
    formatApplicationTime (value) { if (value === null || value === '' || value === 0) return '--'; const numeric = Number(value); const date = Number.isFinite(numeric) ? new Date(numeric < 10000000000 ? numeric * 1000 : numeric) : new Date(value); if (Number.isNaN(date.getTime())) return '--'; const pad = number => String(number).padStart(2, '0'); return date.getFullYear() + '-' + pad(date.getMonth() + 1) + '-' + pad(date.getDate()) + ' ' + pad(date.getHours()) + ':' + pad(date.getMinutes()) + ':' + pad(date.getSeconds()) },
    syncCatalog () { if (this.syncing || !this.baseShopId) return; this.syncing = true; syncWechatCategoryCatalog({ mer_shop_id: this.baseShopId }).then(res => { if (res.code === 200) { this.$message.success('更新请求已受理'); this.pollCatalogSync() } else this.$message.error(res.msg || res.message || '更新失败') }).finally(() => { this.syncing = false }) },
    pollCatalogSync () { const shopId = this.baseShopId; const poll = () => getWechatCategoryCatalogSyncStatus({ mer_shop_id: shopId }).then(res => { const status = res.data && res.data.status; if (status === 'succeeded') { this.$message.success('已有权限类目更新完成'); this.loadCatalog(); return true } if (status === 'failed') { this.$message.error((res.data && res.data.message) || '已有权限类目更新失败'); return true } return false }); poll().then(done => { if (!done) setTimeout(() => this.pollCatalogSync(), 3000) }).catch(() => {}) },
    syncEffectiveCatalog () { if (!this.baseShopId || this.loadingEffective) return; this.loadingEffective = true; syncWechatEffectiveCategories({ mer_shop_ids: [this.baseShopId] }).then(res => { if (res.code === 200) { this.$message.success('已生效类目同步完成'); this.loadEffectiveCatalog() } else this.$message.error(res.msg || res.message || '同步失败') }).finally(() => { this.loadingEffective = false }) },
    statusText (status) { return ({ queued: '排队中', submitting: '提交中', reviewing: '审核中', succeeded: '已通过', failed: '失败', skipped: '已跳过', running: '执行中', partially_succeeded: '部分成功', pending: '待处理' })[status] || status || '--' },
    reasonText (reason) { return ({ qualification_required: '需行业资质', qualification_unknown: '资质状态未知', deposit_exceeded: '保证金超限', deposit_unknown: '保证金未知', already_effective: '已生效', already_applying: '申请中', shop_unavailable: '店铺不可用', shop_forbidden: '无店铺权限', category_not_found: '类目不存在', not_leaf_category: '非叶子类目', eligible: '可申请' })[reason] || reason || '--' },
    qualificationHint () { this.$message.info('该类目申请需要上传证照资料，请到微信小店后台进行申请上传。') }
  }
}
</script>

<style scoped>
.wx-category { min-height: calc(100vh - 78px); padding: 28px 32px 104px; background: #f7f8fb; color: #1a1a2e; }
.wx-category__shop-filter { display: flex; align-items: center; gap: 10px; margin-bottom: 14px; }.wx-category__shop-filter-label { color: #4e5662; font-size: 13px; font-weight: 600; }.wx-category__shop-filter .el-select { width: 230px; }
.wx-category__tabs { display: flex; gap: 26px; border-bottom: 1px solid #e4e7ef; margin-bottom: 18px; }.wx-category__tabs button { border: 0; background: none; padding: 11px 2px; color: #7c8192; cursor: pointer; }.wx-category__tabs button.on { color: #a27623; border-bottom: 2px solid #b28b35; font-weight: 600; }
.wx-category__toolbar,.wx-category__record-toolbar { display: flex; align-items: center; gap: 10px; margin-bottom: 12px; }.wx-category__toolbar .el-select { width: 230px; }.wx-category__toolbar .el-input { width: 400px; }.wx-category__sync-hint { margin-left: auto; color: #7c8192; font-size: 12px; white-space: nowrap; }.wx-category__sync { border: 0; background: transparent; padding: 8px 2px; color: #a27623; cursor: pointer; font-size: 13px; font-weight: 600; }.wx-category__sync:disabled { color: #a7afb8; cursor: not-allowed; }
.wx-category__picker-card,.wx-category__shops { background: #fff; border: 1px solid #e5e8ef; border-radius: 6px; }.wx-category__picker-head { display: flex; justify-content: space-between; align-items: center; padding: 12px 16px; border-bottom: 1px solid #edf0f3; color: #3e4652; font-size: 14px; font-weight: 600; }.wx-category__picker-tools { display: flex; align-items: center; gap: 13px; font-size: 12px; font-weight: 400; }.wx-category__picker-tools button,.wx-category__shop-controls button,.wx-category__record-toolbar button { border: 0; background: transparent; padding: 7px 2px; color: #a27623; cursor: pointer; font-size: 12px; }
.wx-category__columns { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); padding: 0 10px 11px; }.wx-category__column { min-width: 0; border-right: 1px solid #e9edf1; }.wx-category__column:last-child { border-right: 0; }.wx-category__column-head { display: flex; justify-content: space-between; align-items: center; padding: 13px 12px 9px; color: #636b78; font-size: 12px; }.wx-category__column-select { display: flex; align-items: center; gap: 7px; color: #39414c; font-size: 13px; font-weight: 600; }.wx-category input[type='checkbox'] { box-sizing: border-box; display: inline-block !important; flex: 0 0 16px; width: 16px !important; min-width: 16px; height: 16px !important; margin: 0; padding: 0; border: 1px solid #c4cbd4 !important; border-radius: 3px; background: #fff !important; appearance: none !important; -webkit-appearance: none !important; cursor: pointer; vertical-align: middle; }.wx-category input[type='checkbox']:checked { border-color: #b28b35 !important; background-color: #b28b35 !important; background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3E%3Cpath d='M3 8.5 6.5 12 13 4.5' fill='none' stroke='%23fff' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E") !important; background-position: center; background-repeat: no-repeat; background-size: 13px 13px; }.wx-category input[type='checkbox']:indeterminate { border-color: #b28b35 !important; background-color: #b28b35 !important; background-image: linear-gradient(#fff, #fff) !important; background-position: center; background-repeat: no-repeat; background-size: 9px 2px; }.wx-category input[type='checkbox']:focus-visible { outline: 2px solid rgba(178, 139, 53, .35); outline-offset: 2px; }.wx-category input[type='checkbox']:disabled { border-color: #d9dee5 !important; background: #f2f4f7 !important; cursor: not-allowed; opacity: .7; }.wx-category input[type='checkbox']:checked:disabled, .wx-category input[type='checkbox']:indeterminate:disabled { border-color: #d9dee5 !important; background-color: #d9dee5 !important; }.wx-category__column-list { height: 430px; padding: 0 6px; overflow-y: auto; }.wx-category__column-empty { display: flex; align-items: center; justify-content: center; height: 100%; padding: 20px; color: #b0b6bf; font-size: 12px; text-align: center; }
.wx-category__category-row { display: flex; align-items: center; gap: 7px; min-height: 40px; margin: 1px 0; padding: 0 8px; border-radius: 2px; color: #4e5662; cursor: pointer; font-size: 13px; outline: none; transition: background-color .18s ease, color .18s ease; }.wx-category__category-row:hover,.wx-category__category-row:focus-visible,.wx-category__category-row.active { background: #fcf8ef; color: #a27623; }.wx-category__category-row.disabled { color: #aab1ba; }.wx-category__category-name { min-width: 0; flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }.wx-category__category-name em { color: #99a1ab; font-size: 11px; font-style: normal; }.wx-category__category-deposit { flex: 0 0 auto; color: #7f8792; font-size: 11px; white-space: nowrap; }.wx-category__category-row.active .wx-category__category-deposit { color: #a27623; }.wx-category__category-arrow { flex: 0 0 auto; color: #9ba3ad; font-size: 19px; line-height: 1; }.wx-category__tag { flex: 0 0 auto; border: 1px solid #e8c2ae; border-radius: 3px; padding: 1px 4px; color: #b35b32; font-size: 10px; }.wx-category__disabled { flex: 0 0 auto; color: #a0a5b1; font-size: 10px; }
.wx-category__switches { display: flex; flex-wrap: wrap; gap: 22px; padding: 11px 14px 13px; border-top: 1px solid #edf0f3; }.wx-category__switches .el-checkbox { margin-right: 0; color: #4e5662; font-size: 12px; }.wx-category__shops { margin-top: 12px; padding-bottom: 8px; }.wx-category__shops-head { display: flex; justify-content: space-between; padding: 11px 16px; border-bottom: 1px solid #edf0f3; color: #3e4652; font-size: 14px; font-weight: 600; }.wx-category__shop-controls { display: flex; align-items: center; gap: 10px; padding: 10px 14px 3px; }.wx-category__shop-controls .el-input { width: 240px; }.wx-category__shop-controls .el-select { width: 180px; }.wx-category__shop-list { display: flex; flex-wrap: wrap; max-height: 126px; gap: 2px 18px; overflow-y: auto; padding: 4px 14px 7px; }.wx-category__shop-list .el-checkbox { display: flex; align-items: center; min-height: 32px; margin: 0; }.wx-category__shop-list small { display: block; margin-left: 6px; color: #8f95a3; font-size: 11px; }
.wx-category__shops-dialog { margin-top: 0; }.wx-category__shops-dialog .wx-category__shop-list { max-height: 300px; }
.wx-category__footer { position: fixed; right: 0; bottom: 0; left: 258px; z-index: 5; display: flex; align-items: center; gap: 26px; padding: 13px 32px; background: #fff; border-top: 1px solid #dfe4ea; }.wx-category__footer b { color: #a27623; }.wx-category__footer .primary,.wx-category__footer .secondary { min-width: 96px; border-radius: 4px; padding: 9px 18px; cursor: pointer; font-size: 13px; }.wx-category__footer .primary { margin-left: auto; border: 0; color: #fff; background: #b28b35; }.wx-category__footer .secondary { border: 1px solid #d3dbe3; color: #4e5662; background: #fff; }.wx-category__footer button:disabled { opacity: .5; cursor: not-allowed; }
.wx-category__record-toolbar .el-input { width: 260px; }.wx-category__record-toolbar .el-select { width: 150px; }.wx-category__record-toolbar button { border: 1px solid #d9dde7; border-radius: 4px; padding: 7px 12px; }.wx-category__table-wrap { background: #fff; border: 1px solid #e5e8ef; border-radius: 6px; }.status-succeeded { color: #2f9b68; }.status-failed { color: #c8443c; }.status-reviewing,.status-queued { color: #a27623; }.wx-category__summary { margin-bottom: 12px; }.wx-category__summary b { color: #a27623; }.wx-category__reason-list { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 14px; color: #7c8192; font-size: 12px; }.wx-category__success-message { margin: 0; color: #555b6d; font-size: 18px; }.muted { color: #969bab; font-size: 12px; font-weight: 400; }
@media (max-width: 1100px) { .wx-category { padding: 20px 16px 104px; }.wx-category__toolbar { flex-wrap: wrap; }.wx-category__toolbar .el-input { width: min(400px, 100%); }.wx-category__sync-hint { margin-left: 0; }.wx-category__columns { grid-template-columns: repeat(4, minmax(210px, 1fr)); overflow-x: auto; }.wx-category__column-list { height: 390px; } }
@media (max-width: 700px) { .wx-category { padding: 16px 10px 112px; }.wx-category__tabs button { min-width: 80px; padding: 8px 10px; }.wx-category__shop-controls { flex-wrap: wrap; }.wx-category__shop-controls .el-input,.wx-category__shop-controls .el-select { width: 100%; }.wx-category__footer { left: 48px; gap: 10px; padding: 10px 12px; font-size: 12px; }.wx-category__footer .primary,.wx-category__footer .secondary { min-width: auto; padding: 9px 12px; } }

/* 与店铺工作台新版视觉系统保持一致，避免旧版全局 Element 样式串入本页。 */
.wx-category {
  padding: 20px 20px 104px;
  background: #F4F6F8;
  color: #1A1A2E;
}
.wx-category__shop-filter {
  gap: 8px;
  padding: 10px 14px;
  margin-bottom: 14px;
  background: #FFFFFF;
  border: 1px solid #E0E0EC;
  border-radius: 8px;
  box-shadow: 0 1px 4px rgba(15, 23, 42, .04);
}
.wx-category__shop-filter-label {
  min-width: 52px;
  color: #6A6A8A;
  font-size: 12px;
  font-weight: 400;
}
.wx-category__shop-search-hint {
  color: #8A95B0;
  font-size: 12px;
  white-space: nowrap;
}
.wx-category__tabs {
  gap: 4px;
  margin-bottom: 14px;
  border-bottom-color: #E1E5EE;
}
.wx-category__tabs button {
  position: relative;
  padding: 9px 16px;
  color: #4A4A6A;
  font-size: 12px;
}
.wx-category__tabs button.on {
  color: #D4A946;
  font-weight: 600;
  border-bottom: 0;
}
.wx-category__tabs button.on::after {
  position: absolute;
  right: 0;
  bottom: -1px;
  left: 0;
  height: 2px;
  border-radius: 2px 2px 0 0;
  background: linear-gradient(90deg, #D4A946, #E8C778);
  content: '';
}
.wx-category__toolbar,
.wx-category__record-toolbar {
  gap: 8px;
  padding: 12px 14px;
  margin-bottom: 14px;
  background: #FFFFFF;
  border: 1px solid #E1E5EE;
  border-radius: 10px;
  box-shadow: 0 1px 4px rgba(15, 23, 42, .04);
}
.wx-category__sync-hint {
  color: #6A6A8A;
}
.wx-category__sync {
  color: #D4A946;
}
.wx-category__picker-card,
.wx-category__shops,
.wx-category__table-wrap {
  border-color: #E1E5EE;
  border-radius: 10px;
  box-shadow: 0 1px 4px rgba(15, 23, 42, .04);
}
.wx-category__picker-head,
.wx-category__shops-head {
  color: #1A1A2E;
}
.wx-category__picker-title {
  margin: 0;
  color: inherit;
  font: inherit;
}
.wx-category__picker-tools button,
.wx-category__shop-controls button,
.wx-category__record-toolbar button {
  color: #D4A946;
}
.wx-category__record-toolbar button:disabled {
  color: #A7AFB8;
  cursor: not-allowed;
  opacity: .7;
}
.wx-category__column-head {
  color: #6A6A8A;
}
.wx-category__column-select {
  color: #4A4A6A;
}
.wx-category__category-row.active {
  background: rgba(212, 169, 70, .1);
  color: #D4A946;
}
.wx-category__category-row:hover,
.wx-category__category-row:focus-visible {
  background: rgba(212, 169, 70, .1);
  color: #D4A946;
}
.wx-category__category-row.active .wx-category__category-deposit {
  color: #D4A946;
}
.wx-category__switches {
  gap: 18px;
  padding: 12px 14px;
  border-top-color: #E1E5EE;
}
.wx-category__switches .el-checkbox {
  color: #4A4A6A;
  font-size: 12px;
}
.wx-category::v-deep(.wx-category__switches .el-checkbox__label) {
  color: #4A4A6A !important;
  font-size: 12px;
}
.wx-category::v-deep(.wx-category__switches .el-checkbox__input .el-checkbox__inner) {
  width: 14px;
  height: 14px;
  border-color: #D9C98D;
  border-radius: 3px;
}
.wx-category::v-deep(.wx-category__switches .el-checkbox__input.is-checked .el-checkbox__inner),
.wx-category::v-deep(.wx-category__switches .el-checkbox__input.is-indeterminate .el-checkbox__inner) {
  border-color: #D4A946;
  background: #D4A946;
}
.wx-category::v-deep(.wx-category__switches .el-checkbox__input.is-checked + .el-checkbox__label) {
  color: #4A4A6A !important;
}
.wx-category::v-deep(.el-input__inner) {
  border-color: #E0E0EC;
  border-radius: 4px;
  color: #1A1A2E;
}
.wx-category::v-deep(.el-input__inner:hover),
.wx-category::v-deep(.el-input__inner:focus),
.wx-category::v-deep(.el-select .el-input.is-focus .el-input__inner) {
  border-color: rgba(212, 169, 70, .7);
}
.wx-category__footer {
  border-top-color: #E1E5EE;
  box-shadow: 0 -4px 12px rgba(15, 23, 42, .08);
  left: var(--wx-category-sidebar-width, 210px);
}
.wx-category__footer .primary {
  background: linear-gradient(135deg, #D4A946, #E8C778);
}
.wx-category__footer .secondary {
  border-color: rgba(212, 169, 70, .35);
  color: #4A4A6A;
}
.wx-category__footer b,
.wx-category__summary b {
  color: #D4A946;
}
.muted,
.wx-category__reason-list,
.wx-category__progress .muted {
  color: #6A6A8A;
}

/* 首屏紧凑布局：让筛选项在进入页面时即可看到 */
.wx-category {
  min-height: 0;
  padding-bottom: 16px;
}
.wx-category__shop-filter {
  width: fit-content;
  max-width: 100%;
  padding: 8px 12px;
}
.wx-category__tabs {
  margin-bottom: 10px;
}
.wx-category__tabs button {
  padding: 8px 14px;
}
.wx-category__toolbar,
.wx-category__record-toolbar {
  padding: 8px 12px;
  margin-bottom: 10px;
}
.wx-category__picker-head {
  padding: 9px 14px;
}
.wx-category__columns {
  padding-bottom: 8px;
}
.wx-category__column-head {
  padding: 9px 10px 6px;
}
.wx-category__column-list {
  height: max(300px, calc(100vh - 450px));
}
.wx-category__category-row {
  min-height: 36px;
}
.wx-category__switches {
  gap: 14px;
  padding: 9px 12px;
}
.wx-category__footer {
  gap: 18px;
  padding: 9px 20px;
}
.wx-category__footer .primary,
.wx-category__footer .secondary {
  padding: 8px 16px;
}
.wx-category__footer .primary.is-loading {
  cursor: wait;
}
.wx-category__button-spinner {
  display: inline-block;
  width: 12px;
  height: 12px;
  margin-right: 6px;
  border: 2px solid rgba(255, 255, 255, .45);
  border-top-color: #FFFFFF;
  border-radius: 50%;
  vertical-align: -2px;
  animation: wx-category-spin .7s linear infinite;
}
@keyframes wx-category-spin {
  to { transform: rotate(360deg); }
}
.wx-category__footer-summary {
  display: flex;
  align-items: center;
  gap: 18px;
  min-width: 0;
}
.wx-category__footer-summary b,
.wx-category__category-deposit,
.wx-category__summary b {
  font-variant-numeric: tabular-nums;
}
.wx-category__category-row:focus-visible {
  outline: 2px solid rgba(212, 169, 70, .55);
  outline-offset: -2px;
}
.wx-category__tabs button:focus-visible,
.wx-category__sync:focus-visible,
.wx-category__picker-tools button:focus-visible,
.wx-category__shop-controls button:focus-visible,
.wx-category__record-toolbar button:focus-visible,
.wx-category__footer button:focus-visible {
  outline: 2px solid rgba(212, 169, 70, .55);
  outline-offset: 2px;
}
.wx-category button,
.wx-category input[type='checkbox'] {
  touch-action: manipulation;
}
.wx-category::v-deep(.el-input__inner::placeholder) {
  color: #8A95B0;
  opacity: 1;
}
.wx-category__tag {
  border: 1px solid #D9B59C;
  border-radius: 3px;
  padding: 1px 4px;
  color: #A95C37;
  background: transparent;
  cursor: pointer;
  font: inherit;
  font-size: 10px;
  line-height: 1.4;
}
.wx-category__qualification-note {
  color: #A95C37;
  font-size: 12px;
  line-height: 20px;
}
.wx-category__preflight-hint {
  margin: -2px 0 10px;
}
.wx-category__sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
@media (prefers-reduced-motion: reduce) {
  .wx-category__category-row {
    transition: none;
  }
  .wx-category__button-spinner {
    animation-duration: 1.4s;
  }
}
@media (max-width: 1100px) {
  .wx-category__column-list {
    height: max(280px, calc(100vh - 430px));
  }
}
@media (max-width: 700px) {
  .wx-category {
    padding-bottom: 24px;
  }
  .wx-category__shop-filter {
    width: 100%;
    padding: 8px 10px;
    flex-wrap: wrap;
  }
  .wx-category__shop-filter .el-select {
    min-width: 0;
    flex: 1;
    width: auto;
  }
  .wx-category__shop-search-hint {
    flex-basis: 100%;
    margin-left: 52px;
  }
  .wx-category__toolbar,
  .wx-category__record-toolbar {
    padding: 8px 10px;
  }
  .wx-category__column-list {
    height: max(250px, calc(100vh - 400px));
  }
  .wx-category__footer {
    padding: 8px 10px;
  }
  .wx-category__footer-summary {
    gap: 10px;
  }
}
</style>
